import React, { useEffect, useState } from 'react';
import useAnimeReveal from '../hooks/useAnimeReveal';
import useAnimePageLoad from '../hooks/useAnimePageLoad';
import rentalHero from '../assets/AppImages/About Us/about-fleet-hero-v2.webp';
import branchesImage from '../assets/AppImages/home_page/our-vision.jpg';
import ceoImage from '../assets/AppImages/home_page/ceo1.jpg';
import knowHowImage from '../assets/AppImages/home_page/know-how.jpg';
import keysImage from '../assets/AppImages/keys.jpg';
import fleetImage from '../assets/AppImages/products/cars-exec.jpg';
import worldwideMap from '../assets/AppImages/map/map-main.png';

const aboutParagraphs = [
  'Our vision is to become the most customer centric company in car rental industry. We will be persistent in building strong relationships with our customers and business partners.',
  'We constantly focus on innovation and simple, high-quality service so every client can move with confidence, comfort and clarity.',
  'We are convinced that we need to innovate and control the high technology behind our services, and to participate only in markets where we can make significant contribution.',
  "We do not settle for anything less than excellence in every branch office and group in the company. We have courage to admit when we are wrong and courage to change.",
  'We strive to create local opportunity, competitive advantage, growth and impact in every branch office around the world.',
  'Our strategy is to build best-in-class IT platforms that help us stay productive and deliver carefully customized services.',
];

const worldStats = [
  { value: '3', label: 'Years operating', text: 'Three years of focused premium service with a long-term growth mindset.' },
  { value: '6', label: 'Countries', text: 'Branch access across Africa, Europe, the Middle East and Australia.' },
  { value: '7', label: 'Branch offices', text: 'Convenient support points for airport, business and private travel.' },
  { value: '24/7', label: 'Support culture', text: 'A responsive team ready to help before, during and after the journey.' },
];

const operatingPrinciples = [
  {
    icon: 'bi-gem',
    title: 'Luxury',
    text: 'Premium vehicles, clear presentation and a booking experience built around comfort.',
  },
  {
    icon: 'bi-shield-check',
    title: 'Reliability',
    text: 'Fleet checks, clean handovers and branch follow-through that clients can trust.',
  },
  {
    icon: 'bi-stars',
    title: 'Hospitality',
    text: 'A warm, detail-aware service culture shaped around memorable journeys.',
  },
];

const journeyTools = [
  {
    step: '01',
    icon: 'bi-calendar2-check',
    title: 'Reserve with clarity',
    text: 'Clear booking details, route support and vehicle guidance before the trip begins.',
  },
  {
    step: '02',
    icon: 'bi-car-front',
    title: 'Choose the right fleet',
    text: 'Executive sedans, SUVs, twin-cabs and premium options matched to the journey.',
  },
  {
    step: '03',
    icon: 'bi-headset',
    title: 'Stay supported',
    text: 'A responsive team keeps handovers, changes and client questions moving.',
  },
  {
    step: '04',
    icon: 'bi-shield-check',
    title: 'Travel with confidence',
    text: 'Branch standards, fleet checks and careful service protect the experience.',
  },
];

const storySlides = [
  {
    kicker: '01 Our Fleet',
    title: 'Our Fleet',
    text: 'Drive excellence with DeRoyalty Car Rental. Our premium fleet is meticulously maintained, insured and ready to deliver comfort, performance and style for every journey.',
    image: fleetImage,
    points: ['Well maintained', 'Wide selection', 'Best experience'],
  },
  {
    kicker: '02 Our Values',
    title: 'Our Values',
    text: 'We always place the interests of our customers first. We always conduct business with implementation of high standards, trust, honesty, professionalism and ethical behaviour.',
    image: ceoImage,
    points: ['Premium fleet', 'Wide selection', 'Best experience'],
  },
  {
    kicker: '03 Our Vision',
    title: 'Our Vision',
    text: 'Our Vision is to become one of the leading brands in the rental industry of Africa and to build the wide network of branches in the World.',
    image: branchesImage,
    points: ['Leading rental brand', 'African growth', 'Worldwide branch network'],
  },
  {
    kicker: '04 Our Know-How',
    title: 'Our Know-How',
    text: 'DeRoyalty Car Rental is driven by a team of car rental specialists originally from Zimbabwe. Across 3 years operating, Brasco Communications has worked with globally well known companies across Europe, North America and Africa.',
    image: knowHowImage,
    points: ['3 years operating', 'Global standards', 'Trusted service'],
  },
];

const branchOffices = [
  {
    region: 'Africa',
    locations: [
      { country: 'Zimbabwe', city: 'Harare, Bulawayo' },
      { country: 'South Africa', city: 'Johannesburg' },
      { country: 'Angola', city: 'Luanda' },
    ],
  },
  {
    region: 'Europe',
    locations: [{ country: 'United Kingdom', city: 'London' }],
  },
  {
    region: 'Middle East',
    locations: [{ country: 'United Arab Emirates', city: 'Dubai' }],
  },
  {
    region: 'Australia',
    locations: [{ country: 'Australia', city: 'Melbourne' }],
  },
];

const faqItems = [
  {
    question: 'What makes DeRoyalty different?',
    answer: 'We combine premium fleet standards, reliable branch support and a hospitality-led service model for clients who expect a refined rental experience.',
  },
  {
    question: 'Where does DeRoyalty operate?',
    answer: 'Our current footprint includes Zimbabwe, South Africa, Angola, the United Kingdom, the United Arab Emirates and Australia.',
  },
  {
    question: 'Can DeRoyalty support business and leisure travel?',
    answer: 'Yes. Our service approach is built for executive movement, airport transfers, city travel, family trips and high-comfort road journeys.',
  },
];

const mapSrc = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14235.753719474422!2d31.041371909903418!3d-17.895257864334262!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1931a3f22ee38a99%3A0x757348b8f2bdbc19!2sDeRoyalty%20Car%20Rental!5e0!3m2!1sen!2szw!4v1781128963161!5m2!1sen!2szw';
const directionsUrl = 'https://www.google.com/maps/dir/?api=1&destination=-17.770207,31.052499&destination_place_id=DeRoyalty%20Car%20Rental';

function AboutUs() {
  const [activeStory, setActiveStory] = useState(0);

  const rootRef = useAnimePageLoad({ selector: '[data-anime-enter]' });
  useAnimeReveal({ selector: '[data-anime]', rootRef });

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveStory((current) => (current + 1) % storySlides.length);
    }, 6500);

    return () => window.clearInterval(timer);
  }, []);

  const showPreviousStory = () => {
    setActiveStory((current) => (current - 1 + storySlides.length) % storySlides.length);
  };

  const showNextStory = () => {
    setActiveStory((current) => (current + 1) % storySlides.length);
  };

  const openDirections = () => {
    window.open(directionsUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <main className="deroyalty-about-page" ref={rootRef}>
      <section className="deroyalty-about-hero" aria-label="About DeRoyalty Car Rental">
        <img src={rentalHero} alt="Premium DeRoyalty rental fleet outside a modern office" />
        <div className="deroyalty-about-hero-panel" data-anime-enter>
          <p className="deroyalty-eyebrow">Who we are</p>
          <h1>About Us</h1>
          <p className="deroyalty-about-hero-tagline">Driven by excellence. Defined by royal service.</p>
          <p className="deroyalty-about-lede">
            DeRoyalty Car Rental combines a carefully selected fleet with dependable, hospitality-led service
            for business, leisure and executive journeys.
          </p>
          <div className="deroyalty-about-hero-highlights" aria-label="DeRoyalty service highlights">
            {operatingPrinciples.map((item) => (
              <span key={item.title}>
                <i className={`bi ${item.icon}`} aria-hidden="true" />
                {item.title}
              </span>
            ))}
          </div>
          <div className="deroyalty-about-hero-actions">
            <a href="/vehicles#complete-fleet" className="deroyalty-about-primary-action">Browse Fleet</a>
            <a href="/contact" className="deroyalty-about-secondary-action">Contact Us</a>
          </div>
        </div>
      </section>

      <section className="deroyalty-about-section deroyalty-about-resume-layout" data-anime aria-labelledby="about-story-title">
        <div className="deroyalty-about-copy deroyalty-about-card deroyalty-about-summary">
          <p className="deroyalty-eyebrow">Who we are</p>
          <h2 id="about-story-title">A premium rental group with a royal standard.</h2>
          <div className="deroyalty-about-copy-grid">
            {aboutParagraphs.slice(0, 4).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
          <div className="deroyalty-about-principles" aria-label="DeRoyalty service principles">
            {operatingPrinciples.map((item) => (
              <article key={item.title}>
                <i className={`bi ${item.icon}`} aria-hidden="true" />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>

        <aside className="deroyalty-about-card deroyalty-about-toolkit" aria-label="DeRoyalty journey toolkit">
          <p className="deroyalty-eyebrow">Service toolkit</p>
          <h2>Every tool you need for a confident journey.</h2>
          <div className="deroyalty-about-tools-grid">
            {journeyTools.map((tool) => (
              <article className="deroyalty-about-tool-card" key={tool.title}>
                <span>{tool.step}</span>
                <i className={`bi ${tool.icon}`} aria-hidden="true" />
                <h3>{tool.title}</h3>
                <p>{tool.text}</p>
              </article>
            ))}
          </div>
        </aside>
      </section>

      <section className="deroyalty-about-metrics" data-anime data-anime-stagger="true" aria-label="DeRoyalty operating statistics">
        {worldStats.map((stat) => (
          <article className="deroyalty-about-stat" key={stat.label}>
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
            <p>{stat.text}</p>
          </article>
        ))}
      </section>

      <section className="deroyalty-about-section deroyalty-about-standard" data-anime aria-labelledby="about-standard-title">
        <div className="deroyalty-about-section-head">
          <p className="deroyalty-eyebrow">Drive trusted standards</p>
          <h2 id="about-standard-title">Our standard in four moves</h2>
        </div>

        <div className="deroyalty-about-slider" aria-label="DeRoyalty operating standard slider">
          <div
            className="deroyalty-about-slider-track"
            style={{ transform: `translateX(-${activeStory * 100}%)` }}
          >
            {storySlides.map((slide, index) => (
              <article
                className="deroyalty-about-slide"
                key={slide.title}
                aria-hidden={index !== activeStory}
              >
                <div className="deroyalty-about-slide-copy">
                  <p className="deroyalty-eyebrow">{slide.kicker}</p>
                  <h3>{slide.title}</h3>
                  <p>{slide.text}</p>
                  <ul>
                    {slide.points.map((point) => (
                      <li key={point}>
                        <i className="bi bi-check2-circle" aria-hidden="true" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
                <figure className="deroyalty-about-slide-media">
                  <img src={slide.image} alt="" aria-hidden="true" />
                </figure>
              </article>
            ))}
          </div>

          <div className="deroyalty-about-slider-controls">
            <button type="button" onClick={showPreviousStory} aria-label="Previous about story">
              <i className="bi bi-arrow-left" aria-hidden="true" />
            </button>
            <div className="deroyalty-about-slider-dots" aria-hidden="true">
              {storySlides.map((slide, index) => (
                <span key={slide.title} className={index === activeStory ? 'active' : ''} />
              ))}
            </div>
            <button type="button" onClick={showNextStory} aria-label="Next about story">
              <i className="bi bi-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      <section className="deroyalty-about-section deroyalty-about-branches" data-anime aria-labelledby="about-branches-title">
        <div className="deroyalty-about-section-head">
          <p className="deroyalty-eyebrow">Where we move</p>
          <h2 id="about-branches-title">Branch offices</h2>
          <p>
            The business knowledge and know-how we generated across 3 years of focused service gives us serious
            competitive advantage in the industry, especially against rivals.
          </p>
        </div>

        <div className="deroyalty-about-branch-grid" data-anime-stagger="true">
          {branchOffices.map((branch) => (
            <article className="deroyalty-about-branch" key={branch.region}>
              <span>{branch.region}</span>
              <ul>
                {branch.locations.map((location) => (
                  <li key={`${branch.region}-${location.country}`}>
                    <strong>{location.country}</strong>
                    {location.city ? ` - ${location.city}` : ''}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <div className="deroyalty-about-map-card">
          <div className="deroyalty-about-map-copy">
            <h3>Worldwide locations</h3>
            <p>
              We are a premium car rental group in Africa, Australia and the Middle East. Wherever your journey begins,
              there will always be a convenient DeRoyalty Car Rental branch nearby to help you continue your journey.
            </p>
          </div>
          <figure className="deroyalty-about-map">
            <img src={worldwideMap} alt="DeRoyalty worldwide branch map" />
            <svg className="derx-about-map-overlay" viewBox="0 0 400 200" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
              <path
                data-anime-draw
                d="M30,140 C80,100 120,120 170,90 C220,60 270,80 320,50 C350,35 370,45 385,35"
                fill="none"
                stroke="rgba(198, 168, 94, 0.55)"
                strokeWidth="2"
                strokeDasharray="4 6"
              />
            </svg>
          </figure>
        </div>
      </section>

      <section className="deroyalty-about-section deroyalty-about-faq" data-anime aria-labelledby="about-faq-title">
        <div className="deroyalty-about-section-head">
          <p className="deroyalty-eyebrow">Client clarity</p>
          <h2 id="about-faq-title">What clients can expect</h2>
        </div>
        <div className="deroyalty-about-faq-grid" data-anime-stagger="true">
          {faqItems.map((item) => (
            <article className="deroyalty-about-faq-card" key={item.question}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="deroyalty-about-visit" data-anime aria-labelledby="about-visit-title">
        <figure>
          <img src={keysImage} alt="" aria-hidden="true" />
        </figure>
        <div>
          <p className="deroyalty-eyebrow">Visit us at our location</p>
          <h2 id="about-visit-title">2870 Mainway Meadows, Waterfalls, Harare</h2>
          <button type="button" className="deroyalty-about-primary-action" onClick={openDirections}>
            Get Directions to DeRoyalty Car Rental
            <i className="bi bi-geo-alt-fill" aria-hidden="true" />
          </button>
        </div>
      </section>

      <section className="deroyalty-about-map-frame" data-anime data-anime-direction="scale" aria-label="DeRoyalty Google location map">
        <iframe
          src={mapSrc}
          title="DeRoyalty Car Rental location map"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>
    </main>
  );
}

export default AboutUs;
